import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import {
  createReportPermission,
  deleteReportPermission,
  fetchMyDashboardAccess,
  fetchReportAssignments,
  fetchUserAssignments,
  type ReportPermissionCreate,
  type ReportPermissionRead,
} from '@/api/reportPermissions'
import { queryKeys } from '@/lib/queryKeys'
import { toast } from '@/stores/useToastStore'
import { useAuthStore } from '@/stores/useAuthStore'

const STALE_TIME = 5 * 60 * 1000

interface ApiErrorPayload {
  detail?: string
  title?: string
}

function getApiErrorMessage(
  error: AxiosError<ApiErrorPayload>,
  fallback: string
) {
  return error.response?.data?.detail ?? error.response?.data?.title ?? fallback
}

export function useMyDashboardAccess() {
  const accessToken = useAuthStore(state => state.accessToken)

  return useQuery({
    queryKey: queryKeys.dashboards.permissionsMe(),
    queryFn: fetchMyDashboardAccess,
    enabled: Boolean(accessToken),
    staleTime: STALE_TIME,
  })
}

export function useReportAssignments() {
  return useQuery({
    queryKey: queryKeys.dashboards.permissionsByReport(),
    queryFn: fetchReportAssignments,
    staleTime: STALE_TIME,
  })
}

export function useUserAssignments() {
  return useQuery({
    queryKey: queryKeys.dashboards.permissionsByUser(),
    queryFn: fetchUserAssignments,
    staleTime: STALE_TIME,
  })
}

export function useCreateReportPermission() {
  const queryClient = useQueryClient()

  return useMutation<
    ReportPermissionRead,
    AxiosError<ApiErrorPayload>,
    ReportPermissionCreate
  >({
    mutationFn: createReportPermission,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards.all })
      toast.success('Report access granted')
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, 'Failed to grant report access'))
    },
  })
}

export function useDeleteReportPermission() {
  const queryClient = useQueryClient()

  return useMutation<void, AxiosError<ApiErrorPayload>, string>({
    mutationFn: deleteReportPermission,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards.all })
      toast.success('Report access removed')
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, 'Failed to remove report access'))
    },
  })
}
